import React, { useMemo } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function PCO2Chart({ rawData, selectedBuoy, loading }) {
  // Process data for chart
  const chartData = useMemo(() => {
    if (!rawData.length) return []
    
    const grouped = {}
    rawData.forEach(d => {
      if (d._measurement === 'procv_co2' && d._field === 'pCO2' && d.buoy_id === selectedBuoy) {
        const timeKey = new Date(d._time).toISOString().slice(0, 16)
        if (!grouped[timeKey]) grouped[timeKey] = { time: timeKey }
        grouped[timeKey].pCO2 = d._value
        if (d.mode) grouped[timeKey].mode = d.mode
      }
    })

    return Object.values(grouped)
      .sort((a, b) => new Date(a.time) - new Date(b.time))
      .filter(d => typeof d.pCO2 === 'number' && !isNaN(d.pCO2))
  }, [rawData, selectedBuoy])

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    
    const formatLabel = (label) => {
      if (!label) return 'No timestamp'
      const date = new Date(label)
      if (isNaN(date.getTime())) return 'Invalid timestamp'
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    }

    const value = payload[0].value
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        fontSize: '0.9rem',
        padding: '12px',
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          {formatLabel(label)}
        </div>
        <div style={{ color: '#007bff' }}>
          pCO₂: {Number(value).toFixed(2)} μatm
        </div>
      </div>
    )
  }

  return (
    <div style={{ 
      backgroundColor: 'white',
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '1.5rem',
      marginBottom: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{ 
        margin: '0 0 1rem 0',
        color: '#2c3e50',
        textAlign: 'center'
      }}>
        Ocean pCO₂ Measurements from Buoy {selectedBuoy}
      </h3>
      
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: '#6c757d'
        }}>
          Loading real-time data...
        </div>
      ) : chartData.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          color: '#6c757d'
        }}>
          No pCO₂ data available for the selected time range
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ left: 20, right: 20, top: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="time"
              tick={{ fontSize: 12 }}
              tickFormatter={t => t ? t.replace('T', ' ').slice(5, 16) : ''}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{ 
                value: 'pCO₂ (μatm)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="pCO2"
              stroke="#6c757d"
              strokeWidth={2}
              dot={(props) => {
                const mode = props.payload?.mode
                if (mode === 'A M' || mode === 'a_m') {
                  return <circle cx={props.cx} cy={props.cy} r={4} fill="#ff7300" stroke="#fff" strokeWidth={1} />
                } else if (mode === 'W M' || mode === 'w_m') {
                  return <circle cx={props.cx} cy={props.cy} r={4} fill="#0077cc" stroke="#fff" strokeWidth={1} />
                }
                return <circle cx={props.cx} cy={props.cy} r={2} fill="#6c757d" stroke="#fff" strokeWidth={1} />
              }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      
      {/* Legend below chart */}
      {chartData.length > 0 && (
        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          marginTop: '1rem',
          fontSize: '0.9rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#ff7300'
            }}></div>
            <span style={{ color: '#495057' }}>Air pCO₂ measurement</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: '#0077cc'
            }}></div>
            <span style={{ color: '#495057' }}>Water pCO₂ measurement</span>
          </div>
        </div>
      )}
    </div>
  )
}